import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import './StatTile.css';

/* A half-width summary tile: a ring (or an icon), a big number, a label, and — optionally — a
   second line under it. The whole card is the click target, so a tile always leads somewhere.

   It started on the Hercules Trophy participant home as the team-readiness widget and now also
   carries the Bravos company-admin home, which is why it lives here rather than next to either
   page: the markup is generated in one place, so the two homes cannot drift apart.

   Props:
     to / onClick  where the tile leads (both optional, but a tile with neither is a dead card)
     tone          orange | teal | pink | purple — colours the icon chip and, for some tones, the number
     icon          shown when there is no ring
     ring          0-100; draws the conic-gradient donut INSTEAD of the icon
     num           the big number ("42%", 12, "4 / 21")
     trend         optional "+N this week" badge beside the number. Drawn only when it is a
                   positive number — nine tiles each carrying "+0" is noise, and a quiet week
                   should look quiet. A figure with no honest weekly delta (a standing, say)
                   simply never passes one.
     label         the small grey line under it
     sub           an optional third line (e.g. "feedback unlocked")
     note          an optional fourth line, for the half only an admin can act on ("3 waiting
                   for a yes"). Separate from `sub` because the two say different kinds of
                   thing: `sub` describes the tile, `note` asks for something.
     noteTone      'warn' tints the note, for a note that is a job rather than a fact
     locked        drawn greyed with a lock: a module the company has not got. Never a link —
                   `onClick` (when given) opens what to do about it, without it the tile is inert */
const StatTile = ({ to, onClick, tone = 'orange', icon, num, label, sub, note, noteTone,
                    trend, ring, className = '', locked = false }) => (locked ? (
  <button type="button" onClick={onClick} disabled={!onClick}
          className={`stat-tile stat-tile--${tone} stat-tile--locked ${className}`}>
    {icon && (
      <div className="stat-tile__icon">
        <Icon name={icon} />
      </div>
    )}
    <Icon name="lock" className="stat-tile__lock" />
    <div className="stat-tile__label">{label}</div>
    {sub && <div className="stat-tile__sub">{sub}</div>}
  </button>
) : (
  <Link to={to || '#'} onClick={onClick} className={`stat-tile stat-tile--${tone} ${className}`}>
    {ring != null ? (
      <div
        className="stat-tile__ring"
        style={{ background: `conic-gradient(var(--primary-color) ${ring}%, #e9ecef 0)` }}
      >
        <div className="stat-tile__ring-inner" />
      </div>
    ) : (
      icon && (
        <div className="stat-tile__icon">
          <Icon name={icon} />
        </div>
      )
    )}
    {num != null && (
      <div className="stat-tile__num">
        {num}
        {trend > 0 && <span className="stat-tile__trend">+{trend}</span>}
      </div>
    )}
    <div className="stat-tile__label">{label}</div>
    {sub && <div className="stat-tile__sub">{sub}</div>}
    {note && (
      <div className={`stat-tile__note${noteTone ? ` stat-tile__note--${noteTone}` : ''}`}>
        {note}
      </div>
    )}
  </Link>
));

export default StatTile;
